import { Merge } from 'type-fest/index.d';

import { experimentsReducer } from '@hh.ru/front-static-app';
import { BaseRootState } from '@hh.ru/front-static-app/lib/browserApp/modules/baseReducer';
import { autoGeneratedReducer, ReducerObject } from '@hh.ru/redux-create-reducer';

import Currency from 'src/constants/currency';
import TopLevelSite from 'src/constants/topLevelSite';
import areaTree from 'src/models/areaTree';
import competitorAnalysis from 'src/models/competitorAnalysis';
import competitorAnalysisForVacancy from 'src/models/competitorAnalysisForVacancy';
import competitorAnalysisForVacancyTab from 'src/models/competitorAnalysisForVacancyTab';
import competitorAnalysisReport from 'src/models/competitorAnalysisReport';
import competitorAnalysisReportData from 'src/models/competitorAnalysisReportData';
import competitorAnalysisReportTab from 'src/models/competitorAnalysisReportTab';
import config from 'src/models/config';
import currencies from 'src/models/currencies';
import employer from 'src/models/employer';
import employerExperiences from 'src/models/employerExperiences';
import employerManager from 'src/models/employerManager';
import employerManagersList from 'src/models/employerManagersList';
import filterNames from 'src/models/filterNames';
import filterValues from 'src/models/filterValues';
import filterValuesDefault from 'src/models/filterValuesDefault';
import filtersHeight from 'src/models/filtersHeight';
import filtersIsReady from 'src/models/filtersIsReady';
import hiddenColumnsForUser from 'src/models/hiddenColumnsForUser';
import langs from 'src/models/langs';
import maintenanceTillDatetime from 'src/models/maintenanceTillDatetime';
import menu from 'src/models/menu';
import notifications from 'src/models/notifications';
import pageError from 'src/models/pageError';
import pageIsNotAvailable from 'src/models/pageIsNotAvailable';
import pfpInfo from 'src/models/pfpInfo';
import preparedDates from 'src/models/preparedDates';
import profRoleTree from 'src/models/profRoleTree';
import reportName from 'src/models/reportName';
import reportsConfig from 'src/models/reportsConfig';
import request from 'src/models/request';
import salaryMarketRangeData from 'src/models/salaryMarketRangeData';
import trl from 'src/models/trl';
import userType from 'src/models/userType';
import vacancyAdvicesModal from 'src/models/vacancyAdvicesModal';
import vacancyBrandedTemplatesList from 'src/models/vacancyBrandedTemplatesList';
import vacancyConversionDateRanges from 'src/models/vacancyConversionDateRanges';
import vacancyConversionStatistics from 'src/models/vacancyConversionStatistics';
import vacancyCreateRules from 'src/models/vacancyCreateRules';
import vacancyFeatures from 'src/models/vacancyFeatures';
import warningTopBanners from 'src/models/warningTopBanners';

const reducers = {
    areaTree,
    backUrl: autoGeneratedReducer<string>(null),
    csvReports: autoGeneratedReducer<Record<string, string>>(null),
    canViewAllStatistics: autoGeneratedReducer<boolean>(false),
    competitorAnalysis,
    competitorAnalysisReport,
    competitorAnalysisReportData,
    competitorAnalysisReportTab,
    competitorAnalysisForVacancy,
    competitorAnalysisForVacancyTab,
    config,
    countriesEnableReportsList: autoGeneratedReducer<string[]>([]),
    currencies,
    currencyFromBilling: autoGeneratedReducer<Currency>(null),
    employer,
    employerExperiences,
    employerManager,
    employerManagersList,
    errorCode: autoGeneratedReducer<400 | 403 | 404 | 500 | 599 | null>(null),
    experiments: experimentsReducer,
    features: autoGeneratedReducer<Record<string, boolean>>({}),
    fetchingData: autoGeneratedReducer<boolean>(false),
    filterNames,
    filterValues,
    filterValuesDefault,
    filtersIsReady,
    filtersHeight,
    hhid: autoGeneratedReducer<number>(null),
    hiddenColumnsForUser,
    langs,
    maintenancePages: autoGeneratedReducer<string[]>([]),
    maintenanceTillDatetime,
    menu,
    notifications,
    pageError,
    pageIsNotAvailable,
    permissions: autoGeneratedReducer<Array<string>>([]),
    pfpInfo,
    preparedDates,
    profRoleTree,
    reportName,
    reportsConfig,
    request,
    salaryMarketRangeData,
    topLevelSite: autoGeneratedReducer<TopLevelSite>(TopLevelSite.RU),
    trl,
    vacancyBrandedTemplatesList,
    vacancyCreateRules,
    userFeatures: autoGeneratedReducer<Array<string>>(null),
    userId: autoGeneratedReducer<number>(null),
    userType,
    uxfeedback: autoGeneratedReducer<{
        iframeSrc: string;
        iframeOrigin: string;
    }>(null),
    vacancyConversionDateRanges,
    vacancyConversionStatistics,
    vacancyFeatures,
    vacancyAdvicesModal,
    isBackoffice: autoGeneratedReducer<boolean>(false),
    backofficeParams: autoGeneratedReducer<Record<string, string>>({}),
    warningTopBanners,
};

type Store<R = typeof reducers> = { [K in keyof R]: R[K] extends ReducerObject<infer D, unknown> ? D : never };
type FullRootState = Merge<Store, BaseRootState>;

export type Slice<SliceNames extends keyof Store> = {
    [Slice in SliceNames]: Store[Slice] extends Partial<infer D> ? D : never;
};

declare module 'react-redux' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    export interface DefaultRootState extends FullRootState {}
}

export default reducers;
